import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "./PrivacyPolicy.css";

const SECTIONS = [
  {
    id: "collect",
    num: "01",
    icon: "fa-database",
    title: "Information We Collect",
    color: "#2563eb",
    content: null,
    bullets: null,
    custom: "collect",
  },
  {
    id: "use",
    num: "02",
    icon: "fa-gear",
    title: "How We Use Information",
    color: "#0891b2",
    content: "Your information is used to:",
    bullets: [
      // "Provide advisory and financial services",
      "Complete KYC and regulatory compliance",
      "Improve platform functionality",
      "Communicate updates and service information",
      "Comply with legal obligations",
    ],
    note: "We do not sell your personal data.",
  },
  {
    id: "security",
    num: "03",
    icon: "fa-shield-halved",
    title: "Data Security",
    color: "#10b981",
    content:
      "We implement reasonable security measures including encryption, secure servers, and access controls. However, no system can guarantee absolute security.",
  },
  {
    id: "cookies",
    num: "04",
    icon: "fa-cookie-bite",
    title: "Cookies",
    color: "#f59e0b",
    content:
      "We use cookies and similar technologies to improve user experience and analyze platform usage. You may disable cookies through your browser settings at any time.",
  },
  {
    id: "retention",
    num: "05",
    icon: "fa-clock-rotate-left",
    title: "Data Retention",
    color: "#7c3aed",
    content:
      "Information is retained as long as necessary to provide services and comply with legal or regulatory requirements.",
  },
  {
    id: "rights",
    num: "06",
    icon: "fa-user-shield",
    title: "User Rights",
    color: "#ef4444",
    content:
      "You may request access, correction, or deletion of your personal data, subject to applicable laws and regulatory obligations.",
  },
  {
    id: "changes",
    num: "07",
    icon: "fa-rotate",
    title: "Changes to Policy",
    color: "#8b5cf6",
    content:
      "We may update this Privacy Policy from time to time. Continued use of the Platform constitutes acceptance of any changes.",
  },
];

const DATA_ITEMS = [
  { icon: "fa-user", label: "Name", color: "#2563eb" },
  { icon: "fa-envelope", label: "Email Address", color: "#0891b2" },
  { icon: "fa-phone", label: "Phone Number", color: "#10b981" },
  { icon: "fa-id-card", label: "PAN", color: "#f59e0b" },
  { icon: "fa-fingerprint", label: "Aadhaar", color: "#ef4444" },
  { icon: "fa-calendar", label: "Date of Birth", color: "#8b5cf6" },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [active, setActive] = useState("collect");
  const rightPanelRef = useRef(null);

  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;
    const handler = () => {
      const scrollTop = panel.scrollTop + 60;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById("pp-" + SECTIONS[i].id);
        if (el && el.offsetTop <= scrollTop) {
          setActive(SECTIONS[i].id);
          break;
        }
      }
    };
    panel.addEventListener("scroll", handler, { passive: true });
    return () => panel.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    const panel = rightPanelRef.current;
    const el = document.getElementById("pp-" + id);
    if (panel && el) {
      panel.scrollTo({ top: el.offsetTop - 32, behavior: "smooth" });
      setActive(id);
    }
  };

  return (
    <div className="wpp-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="wpp-hero">
        <div className="wpp-hero-bg" />
        <div className="wpp-hero-dotgrid" />
        <div className="wpp-hero-glow wpp-glow-l" />
        <div className="wpp-hero-glow wpp-glow-r" />
        <div className="wpp-hero-inner">
          <div className="wpp-hero-eyebrow">
            <i className="fas fa-lock" /> Privacy
          </div>
          <h1 className="wpp-hero-title">Privacy Policy</h1>
          <p className="wpp-hero-sub">
            Your privacy matters to us. This policy explains what data we
            collect, how we use it, and the rights you have over your personal
            information.
          </p>
          <div className="wpp-hero-meta">
            <span className="wpp-meta-chip">
              <i className="fas fa-calendar-check" /> Effective: January 2025
            </span>
            <span className="wpp-meta-chip">
              <i className="fas fa-shield-halved" /> Data Protected
            </span>
            <span className="wpp-meta-chip">
              <i className="fas fa-list-ol" /> 7 Sections
            </span>
          </div>
        </div>
      </section>

      {/* SPLIT PANEL */}
      <div className="wpp-split">
        <div className="wpp-split-bg" />
        <div className="wpp-split-inner">
          {/* LEFT: fixed TOC */}
          <aside className="wpp-toc">
            <div className="wpp-toc-card">
              <p className="wpp-toc-heading">
                <i className="fas fa-list" /> Table of Contents
              </p>
              <nav className="wpp-toc-nav">
                {SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    className={
                      "wpp-toc-item" +
                      (active === s.id ? " wpp-toc-active" : "")
                    }
                    onClick={() => scrollTo(s.id)}
                    style={
                      active === s.id
                        ? { color: s.color, borderLeftColor: s.color }
                        : {}
                    }
                  >
                    <span className="wpp-toc-num">{s.num}</span>
                    <span className="wpp-toc-lbl">{s.title}</span>
                  </button>
                ))}
              </nav>
              <div className="wpp-toc-cta">
                <p>Privacy concerns?</p>
                <button
                  className="wpp-toc-btn"
                  onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.PRIVACY))}
                >
                  Contact Us <i className="fas fa-arrow-right" />
                </button>
              </div>
            </div>
          </aside>

          {/* RIGHT: independently scrollable */}
          <main className="wpp-content" ref={rightPanelRef}>
            <div className="wpp-content-inner">
              {/* Intro notice */}
              <div className="wpp-notice">
                <i className="fas fa-circle-info" />
                <p>
                  By using the Wealth Compounders platform, you consent to the
                  collection and use of information as described in this{" "}
                  <strong>Privacy Policy</strong>. We are committed to
                  protecting your personal data at all times.
                </p>
              </div>

              {SECTIONS.map((s, i) => (
                <div
                  key={s.id}
                  id={"pp-" + s.id}
                  className="wpp-section"
                  style={{ animationDelay: i * 50 + "ms" }}
                >
                  <div className="wpp-sec-head">
                    <div
                      className="wpp-sec-icon"
                      style={{
                        background: s.color + "12",
                        border: "1.5px solid " + s.color + "22",
                        color: s.color,
                      }}
                    >
                      <i className={"fas " + s.icon} />
                    </div>
                    <div className="wpp-sec-title-group">
                      <span className="wpp-sec-num" style={{ color: s.color }}>
                        {s.num}
                      </span>
                      <h2 className="wpp-sec-title">{s.title}</h2>
                    </div>
                    <div
                      className="wpp-sec-badge"
                      style={{
                        background: s.color + "10",
                        color: s.color,
                        border: "1px solid " + s.color + "25",
                      }}
                    >
                      Section {s.num}
                    </div>
                  </div>

                  <div
                    className="wpp-sec-body"
                    style={{ borderLeftColor: s.color + "45" }}
                  >
                    {/* Special: data collection grid */}
                    {s.custom === "collect" && (
                      <>
                        <p
                          className="wpp-sec-text"
                          style={{ marginBottom: 18 }}
                        >
                          We may collect the following personal information when
                          you register or use our platform:
                        </p>
                        <div className="wpp-data-grid">
                          {DATA_ITEMS.map((d) => (
                            <div
                              key={d.label}
                              className="wpp-data-chip"
                              style={{
                                background: d.color + "0d",
                                border: "1.5px solid " + d.color + "25",
                              }}
                            >
                              <div
                                className="wpp-data-icon"
                                style={{
                                  color: d.color,
                                  background: d.color + "15",
                                }}
                              >
                                <i className={"fas " + d.icon} />
                              </div>
                              <span
                                style={{
                                  color: "#0f172a",
                                  fontWeight: 700,
                                  fontSize: "0.88rem",
                                }}
                              >
                                {d.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Normal text */}
                    {!s.custom && s.content && (
                      <p className="wpp-sec-text">{s.content}</p>
                    )}

                    {/* Bullet list */}
                    {s.bullets && (
                      <ul className="wpp-bullets">
                        {s.bullets.map((b) => (
                          <li key={b} className="wpp-bullet-item">
                            <span
                              className="wpp-bullet-dot"
                              style={{ background: s.color }}
                            />
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Note (no-sell badge) */}
                    {s.note && (
                      <div className="wpp-note">
                        <i className="fas fa-circle-check" />
                        <span>{s.note}</span>
                      </div>
                    )}
                  </div>

                  {i < SECTIONS.length - 1 && (
                    <div className="wpp-sec-divider" />
                  )}
                </div>
              ))}

              {/* Commitment block */}
              <div className="wpp-commitment">
                <div className="wpp-commit-icon">
                  <i className="fas fa-lock" />
                </div>
                <div>
                  <h3>Our Commitment to You</h3>
                  <p>
                    Wealth Compounders is committed to handling your data
                    responsibly and transparently. We will never sell your
                    personal information to third parties. For any
                    privacy-related queries, reach out to us directly.
                  </p>
                  <button
                    className="wpp-commit-btn"
                    onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.PRIVACY))}
                  >
                    Get in Touch <i className="fas fa-arrow-right" />
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
